// API keys of the account for the public API (administrators only; agents never see this). A new
// key is shown once, in the dialog that created it; the list shows its prefix, scopes and use.

import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  EmptyState,
  FormField,
  Input,
  Spinner,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from '@waforms/ui';
import { KeyRound, Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { API_SCOPES, type ApiScope, DEFAULT_SCOPES, SCOPE_DESCRIPTIONS } from '../../shared/api-scopes.ts';
import type { ApiKeyDto } from '../../shared/api-types.ts';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '../lib/api.ts';
import { timeAgo } from '../lib/format.ts';
import { Callout, CopyButton } from './common.tsx';

export function ApiKeysCard({ apiUrl }: { apiUrl?: string }) {
  const keys = useApiKeys(true);
  const [creating, setCreating] = useState(false);

  return (
    <Card
      flush
      title="API keys"
      description="For your own systems: create forms, send them and read the answers through the WhatsApp Forms API. Keys belong to the account, not to one form."
      actions={
        <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
          Create key
        </Button>
      }
    >
      {apiUrl && (
        <div className="flex flex-wrap items-center gap-2 px-5 pb-3 text-label-small text-n-slate-11">
          API base URL <code className="rounded bg-n-alpha-2 px-1.5 py-0.5">{apiUrl}</code>
          <CopyButton value={apiUrl} />
          <a href={`${apiUrl}/openapi.json`} target="_blank" rel="noreferrer">
            OpenAPI
          </a>
        </div>
      )}
      {keys.isPending ? (
        <div className="flex justify-center py-8 text-n-slate-11">
          <Spinner label="Loading API keys" />
        </div>
      ) : keys.isError ? (
        <p role="alert" className="m-0 px-5 pb-5 text-body-main text-n-ruby-11">
          Could not load API keys: {keys.error.message}
        </p>
      ) : keys.data.length === 0 ? (
        <EmptyState
          icon={<KeyRound />}
          title="No API keys"
          description="Create one to use the API. It is shown once, so keep it somewhere safe."
        />
      ) : (
        <div className="px-5 pb-2">
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Key</TH>
                <TH>Scopes</TH>
                <TH>Created</TH>
                <TH>Last used</TH>
                <TH>
                  <span className="sr-only">Actions</span>
                </TH>
              </TR>
            </THead>
            <TBody>
              {keys.data.map((key) => (
                <ApiKeyRow key={key.id} apiKey={key} />
              ))}
            </TBody>
          </Table>
        </div>
      )}
      <CreateApiKeyDialog open={creating} onOpenChange={setCreating} />
    </Card>
  );
}

function ApiKeyRow({ apiKey }: { apiKey: ApiKeyDto }) {
  const revoke = useRevokeApiKey();
  const { toast } = useToast();
  const revoked = apiKey.revokedAt !== null;
  return (
    <TR className={revoked ? 'opacity-60' : undefined}>
      <TD className="font-medium text-n-slate-12">{apiKey.name}</TD>
      <TD className="whitespace-nowrap">
        <code>{apiKey.prefix}…</code>
      </TD>
      <TD>
        <div className="flex flex-wrap gap-1">
          {apiKey.scopes.map((scope) => (
            <Badge key={scope} color={scope.endsWith(':read') ? 'slate' : 'iris'}>
              {scope}
            </Badge>
          ))}
        </div>
      </TD>
      <TD className="whitespace-nowrap">
        <time dateTime={apiKey.createdAt} title={new Date(apiKey.createdAt).toLocaleString()}>
          {timeAgo(apiKey.createdAt)}
        </time>
        <div className="text-label-small text-n-slate-10">{apiKey.createdBy.name}</div>
      </TD>
      <TD className="whitespace-nowrap">{apiKey.lastUsedAt ? timeAgo(apiKey.lastUsedAt) : 'Never'}</TD>
      <TD className="whitespace-nowrap text-end">
        {revoked ? (
          <Badge color="ruby">Revoked</Badge>
        ) : (
          <Button
            size="xs"
            variant="faded"
            color="ruby"
            loading={revoke.isPending}
            onClick={() => {
              if (!window.confirm(`Revoke “${apiKey.name}”? Requests with it stop working at once.`)) return;
              revoke.mutate(apiKey.id, {
                onError: (error) =>
                  toast({ title: 'Could not revoke', description: error.message, tone: 'error' }),
              });
            }}
          >
            Revoke
          </Button>
        )}
      </TD>
    </TR>
  );
}

function CreateApiKeyDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateApiKey();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>([...DEFAULT_SCOPES]);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);

  const close = () => {
    onOpenChange(false);
    setName('');
    setScopes([...DEFAULT_SCOPES]);
    setError(null);
    setSecret(null);
    create.reset();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return setError('Name the key, e.g. after the system that uses it.');
    if (scopes.length === 0) return setError('Pick at least one scope.');
    setError(null);
    create.mutate(
      { name: name.trim(), scopes },
      { onSuccess: (created) => setSecret(created.key), onError: (failure) => setError(failure.message) },
    );
  };

  if (secret) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => !next && close()}
        title="Copy your API key"
        description="This is the only time it is shown. Store it in your system’s secret store."
        footer={<Button onClick={close}>Done</Button>}
      >
        <div className="flex flex-col gap-3">
          <code data-testid="new-api-key" className="break-all rounded bg-n-alpha-2 px-2 py-1.5">
            {secret}
          </code>
          <div>
            <CopyButton value={secret} label="Copy key" />
          </div>
          <Callout tone="warning" title="Treat it like a password">
            Send it only in the Authorization header (Bearer), from your servers, never from a web page.
          </Callout>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && close()}
      title="Create an API key"
      dismissible={!create.isPending}
      footer={
        <>
          <Button variant="faded" color="slate" onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="create-api-key" loading={create.isPending}>
            Create key
          </Button>
        </>
      }
    >
      <form id="create-api-key" onSubmit={submit} className="flex flex-col gap-4">
        <FormField label="Name" hint="Who or what uses it, e.g. “Website leads sync”." required>
          <Input value={name} maxLength={80} onChange={(event) => setName(event.target.value)} />
        </FormField>
        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-1 text-heading-3 text-n-slate-12">Scopes</legend>
          {API_SCOPES.map((scope) => (
            <div key={scope} className="flex flex-col">
              <Checkbox
                label={scope}
                checked={scopes.includes(scope)}
                onChange={(event) =>
                  setScopes((current) =>
                    event.target.checked
                      ? API_SCOPES.filter((entry) => entry === scope || current.includes(entry))
                      : current.filter((entry) => entry !== scope),
                  )
                }
              />
              <span className="ms-7 text-label-small text-n-slate-11">{SCOPE_DESCRIPTIONS[scope]}</span>
            </div>
          ))}
        </fieldset>
        {error && (
          <p role="alert" className="m-0 text-body-main text-n-ruby-11">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}
